import { Controller } from "@hotwired/stimulus"
import { answerItemTemplate } from "./templates/answer_templates"

// Connects to data-controller="question"
export default class extends Controller {
  static targets = [
    "questionInput",
    "activeToggle",
    "mandatoryToggle",
    "hiddenInput",
    "answerInput",
    "answersContainer",
    "questionError",
    "predefinedAnswersError"
  ]
  static values = {
    index: Number,
    question: Object,
    predefinedAnswerErrors: Object,
    questionErrors: Object
  }

  connect() {
    this.handleAnswersUploaded = this.handleAnswersUploaded.bind(this)

    this.updateHiddenInput()
    this.highlightAnswerErrors()
    this.highlightQuestionErrors()

    this.element.addEventListener("optional-questions:answers-uploaded", this.handleAnswersUploaded)
  }

  disconnect() {
    this.element.removeEventListener("optional-questions:answers-uploaded", this.handleAnswersUploaded)
  }

  updateQuestion(event) {
    this.updateField("question", event.target.value)

    if (this.questionErrorsValue && this.questionErrorsValue.question) {
      delete this.questionErrorsValue.question
      this.removeQuestionError()
    }
  }

  toggleActive(event) {
    this.updateField("is_active", event.target.checked)
  }

  toggleMandatory(event) {
    this.updateField("is_required", event.target.checked)
  }

  updateField(field, value) {
    this.questionValue = {
      ...this.questionValue,
      [field]: value
    }
    this.updateHiddenInput()
    this.dispatchEvent("update", {
      index: this.indexValue,
      field: field,
      value: value
    })
  }

  delete(event) {
    event.preventDefault()
    this.dispatchEvent("remove", { index: this.indexValue })
  }

  addAnswer(event) {
    event.preventDefault()

    const newAnswer = {
      id: Date.now(),
      name: ""
    }

    const updatedAnswers = [...(this.questionValue.predefined_answers || []), newAnswer]
    this.updatePredefinedAnswers(updatedAnswers, true)
  }

  updateAnswer(event) {
    const answerIndex = parseInt(event.target.dataset.answerIndex)

    this.modifyAnswerByIndex(answerIndex, (answer) => ({
      ...answer,
      name: event.target.value
    }))

    if (this.predefinedAnswers[answerIndex]) {
      const answerId = this.predefinedAnswers[answerIndex].id
      if (this.predefinedAnswerErrorsValue[answerId]) {
        delete this.predefinedAnswerErrorsValue[answerId]
        this.removeAnswerError(event.target)
      }
    }
  }

  removeAnswer(event) {
    event.preventDefault()
    const answerIndex = parseInt(event.currentTarget.dataset.answerIndex)

    if (this.predefinedAnswers[answerIndex]) {
      const answerToRemove = this.predefinedAnswers[answerIndex]
      const updatedAnswers = this.questionValue.predefined_answers.filter(answer => answer.id !== answerToRemove.id)
      this.updatePredefinedAnswers(updatedAnswers, true)
    }
  }

  modifyAnswerByIndex(answerIndex, modifier, shouldRefresh = false) {
    if (!this.questionValue.predefined_answers) return

    if (this.predefinedAnswers[answerIndex]) {
      const answerToModify = this.predefinedAnswers[answerIndex]
      const fullIndex = this.questionValue.predefined_answers.findIndex(answer => answer.id === answerToModify.id)

      if (fullIndex !== -1) {
        const updatedAnswers = [...this.questionValue.predefined_answers]
        updatedAnswers[fullIndex] = modifier(updatedAnswers[fullIndex])

        this.updatePredefinedAnswers(updatedAnswers, shouldRefresh)
      }
    }
  }

  updatePredefinedAnswers(updatedAnswers, shouldRefresh = false) {
    this.questionValue = {
      ...this.questionValue,
      predefined_answers: updatedAnswers
    }

    this.updateHiddenInput()

    if (shouldRefresh) {
      this.refreshAnswers()
    }

    this.dispatchEvent("update", {
      index: this.indexValue,
      field: "predefined_answers",
      value: updatedAnswers
    })
  }

  refreshAnswers() {
    if (this.hasAnswersContainerTarget && this.questionValue.predefined_answers) {
      const answerItems = this.predefinedAnswers.map((answer, answerIndex) => {
        const error = this.predefinedAnswerErrorsValue[answer.id]
        return answerItemTemplate({ answer, answerIndex, error })
      }).join("")

      this.answersContainerTarget.innerHTML = answerItems
      this.highlightAnswerErrors()
    }
  }

  updateHiddenInput() {
    if (this.hasHiddenInputTarget) {
      this.hiddenInputTarget.value = JSON.stringify(this.questionValue)
    }
  }

  dispatchEvent(eventName, detail) {
    this.dispatch(eventName, { detail })
  }

  highlightAnswerErrors() {
    if (!this.predefinedAnswerErrorsValue) return

    this.answerInputTargets.forEach((input, index) => {
      const answer = this.predefinedAnswers[index]
      if (!answer) return

      const error = this.predefinedAnswerErrorsValue[answer.id]
      const errorElement = input.parentElement.querySelector(".error")
      if (!errorElement) return

      if (error?.name) {
        this.showErrorMessage(errorElement, error.name)
      } else {
        this.hideErrorMessage(errorElement)
      }
    })
  }

  removeAnswerError(input) {
    const errorElement = input.parentElement.parentElement.querySelector(".error")
    if (errorElement) {
      this.hideErrorMessage(errorElement)
    }
  }

  highlightQuestionErrors() {
    if (this.questionErrorsValue?.question && this.hasQuestionErrorTarget) {
      this.showErrorMessage(this.questionErrorTarget, this.questionErrorsValue.question)
    }

    if (this.questionErrorsValue?.predefined_answers && this.hasPredefinedAnswersErrorTarget) {
      this.showErrorMessage(this.predefinedAnswersErrorTarget, this.questionErrorsValue.predefined_answers)
    }
  }

  removeQuestionError() {
    if (this.hasQuestionErrorTarget) {
      this.hideErrorMessage(this.questionErrorTarget)
    }
    
    if (this.hasPredefinedAnswersErrorTarget) {
      this.hideErrorMessage(this.predefinedAnswersErrorTarget)
    }
  }

  showErrorMessage(element, message) {
    element.textContent = message
    element.classList.remove("hidden")
    element.classList.add("error")
  }

  hideErrorMessage(element) {
    element.classList.add("hidden")
    element.classList.remove("error")
  }

  get predefinedAnswers() {
    return this.questionValue.predefined_answers || []
  }

  handleAnswersUploaded(event) {
    const { questionId, answers } = event.detail

    if (questionId !== String(this.questionValue.id)) return

    const existingAnswers = this.questionValue.predefined_answers || []
    const updatedAnswers = [...existingAnswers, ...answers]

    this.updatePredefinedAnswers(updatedAnswers, true)
  }
}
